<template>
  <div ref="phraseEditor" class="phrase-editor max-w-full">
    <FormLabel v-if="label" :label="label" />

    <div v-if="!multiline" class="monaco-singleline w-full relative border border-solid border-stroke h-9 rounded-4 pt-1 bg-white">
      <MonacoEditor
        width="100%"
        height="100%"
        class="flex-grow h-full"
        style="padding: 5px"
        v-model:value="content"
        @change="emitContent"
        language="markdown"
        :options="singlelineEditorOptions"
        @mount="monacoMounted"
        :class="[showPlaceholder ? 'editor-with-placeholder' : '']"
      ></MonacoEditor>

      <div v-show="showPlaceholder" class="placeholder" @click="onPlaceholderClick">
        {{ placeholder }}
      </div>
    </div>
    <div v-if="multiline" class="monaco-multiline w-full relative border border-solid border-stroke h-40">
      <MonacoEditor
        width="100%"
        height="100%"
        class="flex-grow h-full"
        style="padding: 5px"
        v-model:value="content"
        @change="emitContent"
        language="markdown"
        :options="multilineEditorOptions"
        @mount="monacoMounted"
        :class="[showPlaceholder ? 'editor-with-placeholder' : '']"
      ></MonacoEditor>
      <div v-show="showPlaceholder" class="placeholder text-12" @click="onPlaceholderClick">
        {{ placeholder }}
      </div>
    </div>
  </div>
</template>

<script>
import MonacoEditor from '@guolao/vue-monaco-editor';
import { mapGetters, mapState, mapActions } from 'vuex';

export default {
  name: 'PhraseEditor',
  emits: ['update:modelValue'],
  components: { MonacoEditor },
  props: {
    label: String,
    modelValue: String,
    items: Array,
    placeholder: {
      type: String,
      default: 'Enter text, {} to add variables',
    },
    multiline: Boolean,
    filterDataType: Array,
  },
  data() {
    return {
      content: '',
      editor: null,
      completionProvider: null,
      enterCommand: null,
      f1Command: null,
      pasteCommand: null,
      findWidgetCommand: null,
      multilineEditorOptions: {
        minimap: { enabled: false },
        lineNumbers: 'off',
        contextmenu: false,
        automaticLayout: true,
        scrollBeyondLastLine: false,
        extraEditorClassName: 'editor-pane',
        dragAndDrop: true,
        parameterHints: { enabled: true },
        fontLigatures: false,
        lineNumbersMinChars: 1,
        lineDecorationsWidth: 0,
        renderLineHighlight: 'none',
        formatOnType: true,
        tabSize: 2,
        formatOnPaste: true,
        autoClosingBrackets: false,
        glyphMargin: false,
        folding: false,
        wordWrap: 'on',
        overviewRulerLanes: 0,
        overviewRulerBorder: false,
        hideCursorInOverviewRuler: true,
        scrollBeyondLastColumn: 0,
        // see: https://github.com/microsoft/monaco-editor/issues/1746
        wordBasedSuggestions: false,
        // avoid links underline
        links: false,
        // avoid highlight hover word
        occurrencesHighlight: false,
        cursorStyle: 'line-thin',
        // default selection is rounded
        roundedSelection: false,
        hover: {
          // unit: ms
          // default: 300
          delay: 100,
        },
        acceptSuggestionOnEnter: 'on',
        // if monaco is inside a table, hover tips or completion may casue table body scroll
        fixedOverflowWidgets: true,
      },
      singlelineEditorOptions: {
        minimap: { enabled: false },
        lineNumbers: 'off',
        contextmenu: false,
        automaticLayout: true,
        scrollBeyondLastLine: false,
        extraEditorClassName: 'editor-pane',
        dragAndDrop: true,
        parameterHints: { enabled: true },
        fontLigatures: false,
        lineNumbersMinChars: 0,
        lineDecorationsWidth: 0,
        renderLineHighlight: 'none',
        formatOnType: true,
        tabSize: 2,
        formatOnPaste: true,
        autoClosingBrackets: false,
        glyphMargin: false,
        folding: false,
        wordWrap: 'off',
        scrollbar: {
          horizontal: 'hidden',
          vertical: 'hidden',
          // avoid can not scroll page when hover monaco
          alwaysConsumeMouseWheel: false,
        },
        overviewRulerLanes: 0,
        overviewRulerBorder: false,
        hideCursorInOverviewRuler: true,
        scrollBeyondLastColumn: 0,
        // disable `Find`
        find: {
          addExtraSpaceOnTop: false,
          autoFindInSelection: 'never',
          seedSearchStringFromSelection: false,
        },
        // see: https://github.com/microsoft/monaco-editor/issues/1746
        wordBasedSuggestions: false,
        // avoid links underline
        links: false,
        // avoid highlight hover word
        occurrencesHighlight: false,
        cursorStyle: 'line-thin',
        // default selection is rounded
        roundedSelection: false,
        hover: {
          // unit: ms
          // default: 300
          delay: 100,
        },
        acceptSuggestionOnEnter: 'on',
        // if monaco is inside a table, hover tips or completion may casue table body scroll
        fixedOverflowWidgets: true,
      },
    };
  },
  computed: {
    ...mapState(['loadedMonacoEditorLanguages']),
    ...mapGetters(['getProjectVariables']),
    options() {
      let variables = Object.keys(this.getProjectVariables()).map((v) => ({
        name: v,
        ...this.getProjectVariables()[v],
      }));

      if (this.filterDataType?.length) {
        variables = variables.filter((v) => this.filterDataType.includes(v.dataType));
      }
      return variables
        .map((v) => ({
          id: v.name,
          label: v.name,
          value: v.name,
          variable: v,
        }))
        .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));
    },
    showPlaceholder() {
      return this.content.length === 0;
    },
  },
  mounted() {
    if (this.modelValue) {
      this.content = this.modelValue;
    }
  },
  unmounted() {
    this.completionProvider?.dispose?.();
    this.f1Action?.dispose?.();
    this.enterAction?.dispose?.();
    this.findWidgetAction?.dispose?.();
    this.pasteAction?.dispose?.();
    this.editor?.dispose?.();
    this.setMonacoLanguageFlag({ language: 'markdown', flag: false });
  },
  methods: {
    ...mapActions(['setMonacoLanguageFlag']),
    monacoMounted(editor, monaco) {
      this.editor = editor;
      // validation settings
      monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
        noSemanticValidation: true,
        noSyntaxValidation: false,
      });
      // compiler options
      monaco.languages.typescript.javascriptDefaults.setCompilerOptions({
        target: monaco.languages.typescript.ScriptTarget.ES6,
        allowNonTsExtensions: true,
      });
      // disable `Find` widget
      // see: https://github.com/microsoft/monaco-editor/issues/287#issuecomment-328371787
      // eslint-disable-next-line no-bitwise
      this.findWidgetAction = editor.addAction({
        id: 'findWidgetAction',
        label: 'findWidgetAction',
        keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.KEY_F], // eslint-disable-line
        run: () => {},
      }); // eslint-disable-line

      // disable `F1` command palette
      this.f1Action = editor.addAction({
        id: 'f1Action',
        label: 'f1Action',
        keybindings: [monaco.KeyCode.F1],
        run: () => {},
      }); // eslint-disable-line

      if (!this.loadedMonacoEditorLanguages?.markdown) {
        this.setMonacoLanguageFlag({ language: 'markdown', flag: true });
        this.completionProvider = monaco.languages.registerCompletionItemProvider('markdown', {
          triggerCharacters: ['{', '.'],
          provideCompletionItems: (model, position) => {
            if (!this.options || this.options.length <= 0) return { suggestions: [] };

            const suggestions = this.buildSuggestions(monaco, this.options, model, position);

            return {
              suggestions,
            };
          },
        });
      }

      if (!this.multiline) {
        // disable press `Enter` in case of producing line breaks
        this.enterAction = editor.addAction({
          id: 'enterAction',
          label: 'enterAction',
          keybindings: [monaco.KeyCode.Enter],
          run: () => {
            // State: https://github.com/microsoft/vscode/blob/1.56.0/src/vs/editor/contrib/suggest/suggestWidget.ts#L50
            const StateOpen = 3;
            // eslint-disable-next-line
            if (editor._contentWidgets['editor.widget.suggestWidget']?.widget?._widget?._state !== StateOpen) {
              this.emitContent();
            } else {
              /**
               * Origin purpose: disable line breaks
               * Side Effect: If defining completions, will prevent `Enter` confirm selection
               * Side Effect Solution: always accept selected suggestion when `Enter`
               *
               * But it is hard to find out the name `acceptSelectedSuggestion` to trigger.
               *
               * Where to find the `acceptSelectedSuggestion` at monaco official documents ?
               * Below is some refs:
               * - https://stackoverflow.com/questions/64430041/get-a-list-of-monaco-commands-actions-ids
               * - command from: https://github.com/microsoft/vscode/blob/e216a598d3e02401f26459fb63a4f1b6365ec4ec/src/vs/editor/contrib/suggest/suggestController.ts#L632-L638
               * - https://github.com/microsoft/vscode/search?q=registerEditorCommand
               * - real list: https://github.com/microsoft/vscode/blob/e216a598d3e02401f26459fb63a4f1b6365ec4ec/src/vs/editor/browser/editorExtensions.ts#L611
               *
               *
               * Finally, `acceptSelectedSuggestion` appears here:
               * - `editorExtensions.js` Line 288
               */
              editor.trigger('', 'acceptSelectedSuggestion');
            }
          },
        });

        // deal with user paste
        // see: https://github.com/microsoft/monaco-editor/issues/2009#issue-63987720
        this.pasteAction = editor.onDidPaste((e) => {
          // multiple rows will be merged to single row
          if (e.endLineNumber <= 1) {
            return;
          }
          let newContent = '';
          const textModel = editor.getModel();
          const lineCount = textModel.getLineCount();
          // remove all line breaks
          for (let i = 0; i < lineCount; i += 1) {
            newContent += textModel.getLineContent(i + 1);
          }
          textModel.setValue(newContent);
          editor.setPosition({ column: newContent.length + 1, lineNumber: 1 });
        });
      }
    },
    buildSuggestions(instance, suggestions, model, position) {
      let results = [];
      let hasParent = false;

      const prevChar = model.getValueInRange({
        startColumn: position.column - 1,
        startLineNumber: position.lineNumber,
        endColumn: position.column,
        endLineNumber: position.lineNumber,
      });
      const hasBracket = prevChar === '{';

      const word = model.getWordUntilPosition(position);
      let text = word.word;

      const prev = model.findPreviousMatch(
        '{',
        {
          lineNumber: 0,
          column: 0,
        },
        false,
        false,
        null,
        true,
      );

      if (prev) {
        text = model.getValueInRange({
          ...prev.range,
          endColumn: position.column,
        });
      }

      text = text.replace('{', '').trim();

      if (text.length === 0) {
        results = suggestions;
      } else {
        const splits = text.split('.').filter((s) => s.trim() !== '');

        // fo => handled below...
        // foo.ba => foo.children.includes(split)
        // foo.bar. => bar.children
        // foo.bar.b => bar.children.includes(split)
        // foo.bar.baz => []
        if (splits.length) {
          hasParent = true;

          let i = 0;
          let children = suggestions;
          const dotEnd = text[text.length - 1] === '.';

          /* eslint-disable no-restricted-syntax, prefer-destructuring, no-lonely-if */
          for (const split of splits) {
            const found = children.find((c) => c.insertText === split);
            const isLast = splits.length - 1 === i++;

            if (dotEnd) {
              if (found?.children?.length) {
                children = found.children;

                if (isLast) {
                  results = children;
                }
              }
            } else {
              if (found) {
                if (isLast) {
                  children = children.filter((s) => s.insertText.includes(text));
                } else if (found.children) {
                  children = found.children;
                }
              }
            }
          }

          results = children;
        } else {
          results = suggestions.filter((s) => s.value.includes(text));
        }
      }
      return results.map((s) => this.createSuggestion(instance, s, hasBracket, hasParent));
    },
    createSuggestion(instance, suggestion, hasBracket, hasParent = false) {
      const showSuggest = suggestion.children?.length;
      let insertBrackets = false;

      let insertText = suggestion.value;
      if (showSuggest) {
        insertText = `${suggestion.value}.`;
        insertBrackets = !hasParent;
      } else {
        if (hasParent) {
          insertText = `${suggestion.value}} `;
          insertBrackets = hasParent;
        } else {
          insertText = `${suggestion.value}} `;
          insertBrackets = true;
        }
      }

      if (insertBrackets) {
        const bracks = hasBracket ? '' : '{';
        insertText = `${bracks}${insertText}`;
      }

      return {
        label: suggestion.label,
        kind: instance.languages.CompletionItemKind.Variable,
        // detail: suggestion.description,
        insertText,
        ...(showSuggest
          ? {
              command: {
                id: 'editor.action.triggerSuggest',
              },
            }
          : {}),
        range: null,
      };
    },
    emitContent() {
      this.$emit('update:modelValue', this.content.replace(/ /gi, ' ').replace(/’/gi, "'").replace(/–/gi, '-')); // eslint-disable-line
    },
    focus() {
      this.editor.focus();
    },
    onPlaceholderClick() {
      // this.editor.layout();
      this.editor.focus();
    },
  },
  watch: {
    modelValue(newValue, oldValue) {
      if (newValue && !oldValue) {
        this.content = this.modelValue;
        this.emitContent();
      }
    },
  },
};
</script>
<style lang="scss">
.phrase-editor {
  .editor-pane {
    height: 100% !important;
    position: relative;
    outline: 0;
  }

  .monaco-singleline {
    display: flex;
    align-items: center;
    position: relative;

    .decorationsOverviewRuler {
      display: none !important;
    }

    .monaco-editor-background {
      top: 0 !important;
    }
  }

  .monaco-multiline {
    display: flex;
    align-items: flex-start;
    position: relative;

    .decorationsOverviewRuler {
      display: none !important;
    }

    .scrollbar.vertical {
      width: 5px !important;
    }
  }

  .placeholder {
    position: absolute;
    left: 0;
    right: 0;
    overflow: hidden;
    line-height: 30px;

    padding-left: 5px;
    padding-right: inherit;

    cursor: text;
    user-select: none;
    font-size: 12px;
    // height: 20px;
    color: #999;
    font-style: italic;
  }
}
</style>
